import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ComponentRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
    CardComponent,
    IframeDialogComponent,
    LoadingSkeletonCardComponent,
    LoadMoreButtonComponent,
    PersonalizePreferenceDialogComponent,
    PlayButtonComponent,
    SubpageHeaderComponent,
} from '@components';
import { HomeSectionLabel } from '@constants';
import { Context, loadClickAnalyticsActions, RecommendationList } from '@coveo/headless/recommendation';
import { environment } from '@environments/environment';
import { StripHtmlPipe, TruncatePipe } from '@pipes';
import {
    HomeRecommendationEngineService,
    KeplerSfdcService,
    SupportRecommendationsService,
    UserService,
} from '@services';
import { DecodeEntitiesPipe } from '@shared/pipes/decode-entities/decode-entities.pipe';
import { CoveoInitService } from '@shared/services/kepler-coveo/coveo-headless/init.service';
import { VimeoService } from '@shared/services/vimeo/vimeo.service';
import {
    extractVimeoId,
    getVideoDuration,
    isVimeoVideo,
    extractWistiaId,
    isWistiaVideo,
    getWistiaThumbnail,
    createWistiaEmbedUrl,
    createYouTubeEmbedUrl,
} from '@shared/utils';
import { generateRandomImageNumber, cardImageUrl as getCardImageUrl } from '@shared/utils/coveo/coveo-utilities';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    EMPTY,
    firstValueFrom,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { HeadlessSearchResult } from 'src/app/search-results/headless-search/adapter/headless-search-result.interface';
import { HeadlesssSearchResultsAdapter } from 'src/app/search-results/headless-search/adapter/headless-search-results.adapter';
import { RecentlyViewedItem } from 'src/app/search-results/headless-search/services/recently-viewed-item.interface';
import { RecentlyViewedService } from 'src/app/search-results/headless-search/services/recently-viewed.service';
import { adaptCoveoResults } from 'src/app/search-results/headless-search/utils/search.uilities';
import { ChildPageRoute, GknRoutePath } from '../../app-routing.enum';
import { Recommendation } from './snowflake-recommendation.interface';
import { RECOMMENDATION_MODAL_CONTENT_URLS, RESOURCES_RECOMMENDATION_URLS } from './snowflake-recommendations.enum';
import { KeplerSnowFlakeRecommendationsService } from './snowflake-recommendations.service';
dayjs.extend(utc);

@Component({
    selector: 'app-home-recommendations',
    imports: [
        CommonModule,
        DecodeEntitiesPipe,
        LoadingSkeletonCardComponent,
        LoadMoreButtonComponent,
        MatCardModule,
        MatIconModule,
        MatTooltipModule,
        MatDialogModule,
        StripHtmlPipe,
        SubpageHeaderComponent,
        TruncatePipe,
        CardComponent,
        PlayButtonComponent,
    ],
    templateUrl: './home-recommendations.component.html',
    styleUrls: ['./home-recommendations.component.scss'],
})
export class HomeRecommendationsComponent implements OnInit, OnDestroy {
    private cdr = inject(ChangeDetectorRef);
    private snowFlakeRecommendationsService = inject(KeplerSnowFlakeRecommendationsService);
    private supportRecommendationsService = inject(SupportRecommendationsService);
    private homeRecommendationEngine = inject(HomeRecommendationEngineService);
    private recentlyViewedService = inject(RecentlyViewedService);
    private keplerSfdcService = inject(KeplerSfdcService);
    private userService = inject(UserService);
    private initService = inject(CoveoInitService);
    private dialog = inject(MatDialog);
    private router = inject(Router);
    private bo = inject(BreakpointObserver);

    private destroy$ = new Subject<void>();
    private recommendationsListController!: RecommendationList;
    private headlessUnsubscribes: (() => void)[] = [];
    private context!: Context;
    adapter: HeadlesssSearchResultsAdapter;

    title = HomeSectionLabel.RECOMMENDATIONS;
    @ViewChild('recommendationsHeaderRef') headerRef: ComponentRef<SubpageHeaderComponent>;

    cards$: Observable<Recommendation[]>;
    coveoRecommendations: HeadlessSearchResult[] = [];
    private refreshTrigger$ = new BehaviorSubject<void>(undefined);
    dialogRef: MatDialogRef<any>;
    isLoading: boolean = true;
    hasResults: boolean = false;

    length = 4;
    max: number;
    modifier = 4;
    isLoadMoreDisabled = false;

    removingCardIds: Set<string> = new Set();
    private removedCardIds: Set<string> = new Set();
    private visitedItems = new Set<string>();
    private loggedRecommendations = new Set<string>();
    showInvertedPlayButton = false;

    constructor() {
        const _vimeoService = inject(VimeoService);

        this.adapter = new HeadlesssSearchResultsAdapter(_vimeoService);
    }

    /**
     * Initialize component, start engine initialization, and set up recommendation streams.
     */
    ngOnInit(): void {
        this.initializeEnginesAndRecommendations();

        const snowflakeRecs$ = this.getSnowflakeRecommendations();
        const homeRecs$ = this.getHomeRecommendations();

        this.cards$ = combineLatest([snowflakeRecs$, homeRecs$]).pipe(
            map(([snowflakeRecs, homeRecommendations]) => {
                const filteredSnowflake = snowflakeRecs.filter(
                    (recommendation: Recommendation) => recommendation.userSilencedDate === null,
                );

                const uniqueSnowflakeRecs = this.getUniqueRecommendations(filteredSnowflake);
                const coveoRecs = this.adaptCoveoRecommendations(homeRecommendations);
                return [...uniqueSnowflakeRecs, ...coveoRecs].filter(
                    (card) => !this.removedCardIds.has(String(card.id)),
                );
            }),
            tap((recommendations: Recommendation[]) => {
                this.max = recommendations.length;
                this.isLoadMoreDisabled = recommendations.length <= this.length;
            }),
            catchError((err) => {
                console.warn('An error occurred while initializing', err);
                return of([]);
            }),
        );

        this.refreshTrigger$.next();
    }

    /**
     * @returns Observable of snowflake recommendations
     */
    private getSnowflakeRecommendations(): Observable<Recommendation[]> {
        return this.refreshTrigger$.pipe(
            switchMap(() => this.snowFlakeRecommendationsService.recommendations$),
            map((recommendations: Recommendation[]) => this.sortNotifications(recommendations || [])),
            map((recommendations: Recommendation[]) => {
                // TODO Do we need to check card.userSilencedDate against "now"?
                return recommendations?.filter((card: Recommendation) => card?.userSilencedDate === null);
            }),
        );
    }

    /**
     * Initialize engines and then set up recommendations
     */
    private async initializeEnginesAndRecommendations(): Promise<void> {
        try {
            await this.initService.init();
            await this.initializeRecommendationList();
        } catch (_error) {
            this.isLoading = false;
            this.hasResults = false;
        }
    }

    /**
     * Initialize the recommendation controller
     */
    private async initializeRecommendationList(): Promise<void> {
        if (!this.homeRecommendationEngine.isInitialized()) {
            return;
        }

        try {
            this.recommendationsListController = this.homeRecommendationEngine.getRecommendationList();
            this.context = this.homeRecommendationEngine.getContext();

            this.userService.isAuthenticated$
                .pipe(
                    switchMap((isAuthed) => {
                        const unsubscribe = this.recommendationsListController.subscribe(async () => {
                            try {
                                const state = this.recommendationsListController.state;
                                this.hasResults = state?.recommendations?.length > 0;
                                const results = adaptCoveoResults({
                                    results: state?.recommendations,
                                    isAuthed,
                                });
                                await this.adapter.fetchVideoDurations(results);
                                this.coveoRecommendations = results;
                                console.debug('Home Coveo Recommendations', this.coveoRecommendations);
                                this.refreshTrigger$.next();
                                this.isLoading = false;
                            } catch (error) {
                                console.error('Error processing recommendations:', error);
                                this.isLoading = false;
                            }
                        });

                        this.headlessUnsubscribes.push(unsubscribe);
                        return of(null);
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe();

            this.recommendationsListController.refresh();
        } catch (_error) {
            this.hasResults = false;
            this.isLoading = false;
        }
    }

    /**
     * Adapt Coveo recommendations to Recommendation interface
     * @param coveoRecs - HeadlessSearchResult array
     * @returns Recommendation array
     */
    private adaptCoveoRecommendations(coveoRecs: HeadlessSearchResult[]): Recommendation[] {
        return coveoRecs.map(
            (recommendation: HeadlessSearchResult) =>
                ({
                    id: `home-coveo-${recommendation.title}`,
                    title: recommendation.title,
                    description: recommendation?.description,
                    url: recommendation?.url,
                    info: recommendation?.sourceDisplayName,
                    userSilencedDate: null,
                    isCoveoRecommendation: true,
                    imageNumber: generateRandomImageNumber(),
                    thumbnail: recommendation?.thumbnail,
                    ytthumbnailurl: recommendation?.ytthumbnailurl,
                    duration: recommendation?.duration,
                    customTag: recommendation?.customTag,
                }) as Recommendation,
        );
    }

    /**
     * Get home recommendations from Coveo recommendation engine
     * @returns Observable of HeadlessSearchResult array
     */
    private getHomeRecommendations(): Observable<HeadlessSearchResult[]> {
        return this.refreshTrigger$.pipe(switchMap(() => of(this.coveoRecommendations)));
    }

    /**
     * Get unique recommendations based on info property
     * @param recommendations - Array of recommendations to filter
     * @returns Array of unique recommendations
     */
    private getUniqueRecommendations(recommendations: Recommendation[]): Recommendation[] {
        const uniqueRecs: Recommendation[] = [];

        for (const recommendation of recommendations) {
            if (!uniqueRecs.find((existing) => existing.info === recommendation.info)) {
                uniqueRecs.push(recommendation);
            }
        }

        return uniqueRecs;
    }

    /**
     * Return Recommendation Image Url
     * @param card - Recommendation card
     * @returns url string
     */
    cardImageUrl(card: Recommendation): string {
        return getCardImageUrl(
            card?.info,
            card?.url,
            card?.imageNumber,
            card?.thumbnail,
            Object.values(RESOURCES_RECOMMENDATION_URLS),
            environment.api.community.baseUrl,
        );
    }

    /**
     * Determine what Recommendation url is based on recommendation info
     * @param card - Recommendation Card
     * @returns url string
     */
    private cardUrlBestGuess(card: Recommendation): string {
        switch (card?.info?.toLowerCase()) {
            case 'education':
                return `/${GknRoutePath.EDUCATION}`;
            case 'advocacy':
                return `/${GknRoutePath.ADVOCACY}`;
            case 'appfoundry':
                return `${GknRoutePath.RESOURCES}/${ChildPageRoute.APPFOUNDRY}`;
            case 'community':
                return `/${GknRoutePath.COMMUNITY}`;
            case 'partner':
                return `${GknRoutePath.RESOURCES}/${ChildPageRoute.GENESYS_NEWS_AND_EVENTS}`;
            case 'resources':
                // there is no root page for Resources, so navigate to the primary child page, Product Knowledge
                return `/${`${GknRoutePath.RESOURCES}/${ChildPageRoute.PRODUCT_KNOWLEDGE}`}`;
            case 'support':
                return `/${GknRoutePath.SUPPORT}`;
            default:
                return `/${GknRoutePath.HOME}`;
        }
    }

    /**
     * Open Preferences Dialog
     */
    openPreferencesDialog(): void {
        this.dialogRef = this.dialog.open(PersonalizePreferenceDialogComponent, {
            maxWidth: '1246px',
            width: '80%',
            height: '80%',
            hasBackdrop: true,
        });
    }

    /**
     * Sort notifications
     * @param cards - Recommendation cards
     * @returns - collection of Recommendations array
     */
    private sortNotifications(cards: Recommendation[]): Recommendation[] {
        return (
            cards?.sort((a, b) => {
                // Prioritize user notifications
                if (a.type === 'user' && b.type === 'account') return -1;
                if (a.type === 'account' && b.type === 'user') return 1;

                // If same type, sort by creation date (newest first)
                if (a.createdDate && b.createdDate) {
                    return dayjs(b.createdDate).diff(dayjs(a.createdDate));
                }
                return 0;
            }) || []
        );
    }

    /**
     * Handle on Card click
     * @param card - Recommendation card
     */
    onCardClick(card: Recommendation): void {
        // Track visited items and log analytics for Coveo recommendations
        if (card.isCoveoRecommendation) {
            this.visitedItems.add(card.url);

            const coveoRec = this.coveoRecommendations.find((rec) => rec.url === card.url);
            if (coveoRec && !this.loggedRecommendations.has(card.url)) {
                this.loggedRecommendations.add(card.url);
                const { logRecommendationOpen } = loadClickAnalyticsActions(this.homeRecommendationEngine.get());
                this.homeRecommendationEngine.get().dispatch(logRecommendationOpen(coveoRec.result));

                this.recentlyViewedService.addView({
                    title: coveoRec.title,
                    url: coveoRec.url,
                    timestamp: coveoRec.timestamp,
                    result: coveoRec.result,
                } as RecentlyViewedItem);
            }
        }

        if (card.url) {
            if (card.url.startsWith('http') || card.url.startsWith('www')) {
                const cardUrl = (card.url.startsWith('www') ? 'https://' : '') + card.url;

                const vimeoId = extractVimeoId(card.url);
                if (vimeoId) {
                    this.openIframeDialog(card.title, `https://player.vimeo.com/video/${vimeoId}`);
                    return;
                }
                const wistiaId = extractWistiaId(card.url);
                if (wistiaId) {
                    this.openIframeDialog(card.title, createWistiaEmbedUrl(wistiaId));
                    return;
                }
                if (card.url.includes('youtube.com') || card.url.includes('youtu.be')) {
                    this.openIframeDialog(card.title, createYouTubeEmbedUrl(card.url));
                    return;
                }

                if (Object.values(RECOMMENDATION_MODAL_CONTENT_URLS).some((url) => card.url.indexOf(url) >= 0)) {
                    firstValueFrom(
                        this.bo.observe('(max-width: 1020px)').pipe(
                            map((state) => state.matches),
                            catchError(() => of(false)),
                        ),
                    )
                        .then((isMobile) => {
                            this.dialog.open(IframeDialogComponent, {
                                width: isMobile ? '' : '80%',
                                maxWidth: '1400px',
                                height: isMobile ? '90%' : '80%',
                                autoFocus: false,
                                data: {
                                    title: card?.title,
                                    src: cardUrl,
                                    dataAnalytics: 'home-recommendation',
                                },
                            });
                        })
                        .catch(() => {
                            window.open(cardUrl, '_blank');
                        });
                } else {
                    window.open(cardUrl, '_blank');
                }
            } else {
                this.router.navigateByUrl(card.url);
            }
        } else {
            this.router.navigateByUrl(this.cardUrlBestGuess(card));
        }
    }

    /**
     * Handle remove card click - subscribes to the removeCard observable
     * @param id - card id
     */
    onRemoveCard(id: any): void {
        this.removeCard(id).pipe(takeUntil(this.destroy$)).subscribe();
    }

    /**
     * Remove home recommendations
     * @param id - card id
     * @returns message indicating success
     */
    removeCard(id: any): Observable<string> {
        const idString = String(id);
        this.removingCardIds.add(idString);

        if (idString.startsWith('support-')) {
            this.supportRecommendationsService.remove(idString);
            this.handleCardRemoval(idString);
        } else if (idString.startsWith('home-coveo-')) {
            this.handleCardRemoval(idString);
        } else {
            return this.keplerSfdcService.email$.pipe(
                switchMap((salesforceEmail) => {
                    if (!salesforceEmail) {
                        console.error('No email found in SFDC details');
                        this.removingCardIds.delete(idString);
                        return of('Error');
                    }
                    console.debug('Using email for card removal:', salesforceEmail);

                    this.handleCardRemoval(idString);

                    return this.snowFlakeRecommendationsService.updateRecommendation(idString, salesforceEmail).pipe(
                        catchError((error) => {
                            console.error('Error updating recommendation:', error);
                            this.removingCardIds.delete(idString);
                            return EMPTY;
                        }),
                    );
                }),
            );
        }

        return of('Success');
    }

    /**
     * Handle card removal UI updates
     * @param id - card id
     */
    private handleCardRemoval(id: string): void {
        this.removedCardIds.add(id);
        this.removingCardIds.delete(id);
        this.refreshTrigger$.next();
    }

    /**
     * Can Card be Removed or Deleted
     * @param cardId - string id
     * @returns boolean
     */
    isCardRemoving(cardId: string): boolean {
        return this.removingCardIds?.has(cardId);
    }

    /**
     * Check if recommendation is a Vimeo video
     * @param card - Recommendation card
     * @returns boolean
     */
    isVimeoVideo = isVimeoVideo;

    /**
     * Check if recommendation is a Wistia video
     * @param card - Recommendation card
     * @returns boolean
     */
    isWistiaVideo = isWistiaVideo;

    /**
     * Get Vimeo thumbnail URL
     * @param card - Recommendation card
     * @returns thumbnail URL
     */
    getVimeoThumbnail(card: Recommendation): string {
        const coveoRec = this.coveoRecommendations.find((rec) => rec.url === card.url);
        if (coveoRec) {
            return this.adapter.getVimeoThumbnail(coveoRec.url) || card?.thumbnail || this.cardImageUrl(card);
        }
        return card?.thumbnail || this.cardImageUrl(card);
    }

    /**
     * Get Wistia thumbnail URL
     * @param card - Recommendation card
     * @returns thumbnail URL
     */
    getWistiaThumbnail(card: Recommendation): string {
        const wistiaId = extractWistiaId(card.url);
        return wistiaId ? getWistiaThumbnail(wistiaId) : card?.thumbnail || this.cardImageUrl(card);
    }

    /**
     * Get video duration
     * @param card - Recommendation card
     * @returns duration string
     */
    getVideoDuration = getVideoDuration;

    /**
     * Refresh loading length
     * @param val - number to load
     */
    refreshLength(val: number): void {
        this.length = val;
        this.cdr.detectChanges();
    }

    /**
     * Open IframeDialog with responsive sizing based on viewport width.
     * Falls back to opening in a new tab if the dialog fails to open.
     * @param title - Dialog title
     * @param src - URL to load in the iframe
     */
    private openIframeDialog(title: string, src: string): void {
        firstValueFrom(
            this.bo.observe('(max-width: 1020px)').pipe(
                map((state) => state.matches),
                catchError(() => of(false)),
            ),
        )
            .then((isMobile) => {
                this.dialog.open(IframeDialogComponent, {
                    width: isMobile ? '' : '80%',
                    maxWidth: '1400px',
                    height: isMobile ? '90%' : '80%',
                    autoFocus: false,
                    data: { title, src, dataAnalytics: 'home-recommendation' },
                });
            })
            .catch(() => {
                window.open(src, '_blank');
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.headlessUnsubscribes?.forEach((unsubscribe) => unsubscribe());
    }
}
